import { z } from 'zod';
import { PUBLIC_EXPERIENCE_SCHEMA_VERSION, PUBLIC_EXPERIENCE_SCHEMA_VERSION_V2, PublicExperienceStatusEnum, PublicExperienceStatusSchema, PublicExperienceSchemaVersionSchema, PublicExperienceSchemaVersionV2Schema, PublicExperienceTemplateSchema, PublicExperienceTypeSchema, PublicExperienceFormTypeSchema, } from './enums';
import { PaymentTermsAuthoringSchema } from './payment-terms';
import { JotformPaymentTimingSchema } from './jotform';
import { OfferDraftSchema, PublicOfferViewSchema, PublishedOfferSchema, PublishedOfferV1Schema, } from './offer';
import { BusinessIdSchema, ExperienceIdSchema, HexColorSchema, HttpsUrlSchema, IsoTimestampSchema, PublicExperiencePublicIdSchema, PublishedRevisionSchema, RevisionSchema, } from './primitives';
/**
 * Public Experiences — experience models.
 *
 * Three distinct shapes, deliberately never shared:
 *  1. `PublicExperienceDocumentSchema`  — private canonical config (server-only
 *     Firestore root `/publicExperiencePrivate/...`).
 *  2. `PublicExperiencePageProjectionSchema` — the allowlisted, world-readable
 *     projection (`/public/experiences/pages/{publicId}`). `.strict()`.
 *  3. `AdminExperience*Schema` — what the authenticated admin API returns.
 */
/** Minimal branding. Display-only; no scripts, no HTML, no arbitrary CSS. */
export const ExperienceBrandingSchema = z
    .object({
    logoUrl: HttpsUrlSchema.nullable().default(null),
    primaryColorHex: HexColorSchema.nullable().default(null),
    /** Optional short tagline rendered under the title. */
    tagline: z.string().trim().max(120).nullable().default(null),
})
    .strict();
export const ExperienceTitleSchema = z.string().trim().min(1).max(120);
export const ExperienceDescriptionSchema = z.string().trim().max(2000);
/** Missing type is resolved by the management service, never defaulted by a historical reader. */
export const ExperienceFormClassificationShape = {
    formType: PublicExperienceFormTypeSchema.optional(),
    formTypeReviewReason: z
        .enum(['HISTORICAL_BINDING', 'MIXED_USE', 'INCONSISTENT_BINDING'])
        .optional(),
};
/**
 * The working copy an admin edits. Editing a draft never changes live behaviour;
 * only `publish` does.
 *
 * `draftRevision` is the optimistic-concurrency token: every write must send the
 * revision it read (`expectedDraftRevision`) and the server increments it. A
 * mismatch is `PXE-010 DRAFT_REVISION_CONFLICT`.
 */
export const ExperienceDraftSchema = z
    .object({
    title: ExperienceTitleSchema,
    internalName: ExperienceTitleSchema.optional(),
    paymentTerms: PaymentTermsAuthoringSchema.optional(),
    /** Setup preference before a real binding exists; never payment authority. */
    jotformPaymentTiming: JotformPaymentTimingSchema.optional(),
    description: ExperienceDescriptionSchema,
    branding: ExperienceBrandingSchema,
    offer: OfferDraftSchema,
    draftRevision: RevisionSchema,
    updatedAt: IsoTimestampSchema,
})
    .strict();
/**
 * The frozen, live copy. `revision` is the `offerRevision` a guest must echo
 * back when creating a checkout; a stale one is rejected with `PXE-003
 * OFFER_REVISION_STALE` rather than silently re-priced.
 */
const ExperiencePublishedBaseShape = {
    revision: PublishedRevisionSchema,
    publishedAt: IsoTimestampSchema,
    publishedBy: z.string().trim().min(1).max(128),
    title: ExperienceTitleSchema,
    description: ExperienceDescriptionSchema,
    branding: ExperienceBrandingSchema,
};
/** Contract 1.0.0 published block (v1 provider linkage), retained verbatim. */
export const ExperiencePublishedV1Schema = z
    .object({
    ...ExperiencePublishedBaseShape,
    offer: PublishedOfferV1Schema,
})
    .strict();
/** Contract 2.0.0 published block: the offer's linkage carries `executionScope`. */
export const ExperiencePublishedSchema = z
    .object({
    ...ExperiencePublishedBaseShape,
    offer: PublishedOfferSchema,
})
    .strict();
/** Reader-side union for admin/merchant detail views. */
export const AnyExperiencePublishedSchema = z.union([
    ExperiencePublishedSchema,
    ExperiencePublishedV1Schema,
]);
const ExperienceDocumentBaseShape = {
    businessId: BusinessIdSchema,
    experienceId: ExperienceIdSchema,
    publicId: PublicExperiencePublicIdSchema,
    type: PublicExperienceTypeSchema,
    formType: PublicExperienceFormTypeSchema.optional(),
    template: PublicExperienceTemplateSchema,
    status: PublicExperienceStatusSchema,
    draft: ExperienceDraftSchema,
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
    /** Super-admin (or merchant) uid that last wrote the document. */
    updatedBy: z.string().trim().min(1).max(128),
};
const requirePublishedUnlessDraft = (doc, ctx) => {
    if (doc.status !== PublicExperienceStatusEnum.DRAFT && !doc.published) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['published'],
            message: 'a PUBLISHED or DISABLED experience must carry a published snapshot',
        });
    }
};
const PublicExperienceDocumentV1ObjectSchema = z
    .object({
    schemaVersion: PublicExperienceSchemaVersionSchema,
    ...ExperienceDocumentBaseShape,
    /** Absent until the first successful publish. */
    published: ExperiencePublishedV1Schema.optional(),
})
    .strict();
const PublicExperienceDocumentObjectSchema = z
    .object({
    schemaVersion: PublicExperienceSchemaVersionV2Schema,
    ...ExperienceDocumentBaseShape,
    /** Absent until the first successful publish under direct charges. */
    published: ExperiencePublishedSchema.optional(),
    /**
     * Set exactly once when a v1 (destination) experience is republished under
     * Part 2: the previous platform-owned published block is preserved here so
     * the historical Product/Price ids stay discoverable. Never updated again;
     * never archived on the provider by Part 2.
     */
    previousPublished: ExperiencePublishedV1Schema.optional(),
})
    .strict();
/**
 * Private canonical experience document — contract 1.0.0 shape, retained
 * verbatim for `schemaVersion: 1` documents (published on the platform account
 * with destination charges).
 *
 * Lives under the server-only root `/publicExperiencePrivate/{businessId}/
 * experiences/{experienceId}` (covered by Firestore's default deny — there is no
 * client read or write path). Timestamps are ISO-8601 strings here too; the
 * repository converts Firestore `Timestamp` at its boundary.
 */
export const PublicExperienceDocumentV1Schema = PublicExperienceDocumentV1ObjectSchema.superRefine(requirePublishedUnlessDraft);
/**
 * Contract 2.0.0 experience document: `schemaVersion: 2`, a published block
 * whose linkage carries the required `executionScope`, and the optional
 * `previousPublished` v1 block. A republish of a v1 experience writes this
 * shape (keeping `publicId`); a v1 document that was never republished keeps
 * parsing as v1 and its checkouts are refused with `PXE-016`.
 */
export const PublicExperienceDocumentSchema = PublicExperienceDocumentObjectSchema.superRefine(requirePublishedUnlessDraft);
/** Discriminated on `schemaVersion` — the only legacy discriminator. */
export const AnyPublicExperienceDocumentSchema = z
    .discriminatedUnion('schemaVersion', [
    PublicExperienceDocumentV1ObjectSchema,
    PublicExperienceDocumentObjectSchema,
])
    .superRefine(requirePublishedUnlessDraft);
/** True when the live linkage was published under direct charges (v2). */
export const isDirectPublishedExperience = (doc) => doc.schemaVersion === PUBLIC_EXPERIENCE_SCHEMA_VERSION_V2 && !!doc.published;
/**
 * Server-only resolver document `/publicExperienceReferences/{publicId}`.
 * Maps a public id to its private location without exposing the mapping.
 */
export const PublicExperienceReferenceDocumentSchema = z
    .object({
    businessId: BusinessIdSchema,
    experienceId: ExperienceIdSchema,
    createdAt: IsoTimestampSchema,
})
    .strict();
/**
 * Status values a public projection may carry. `DRAFT` is impossible by
 * construction: an unpublished experience has no projection document at all.
 */
export const PublicProjectionStatusSchema = z.enum([
    PublicExperienceStatusEnum.PUBLISHED,
    PublicExperienceStatusEnum.DISABLED,
]);
/**
 * The world-readable projection (`allow get: if true; list: false; write: false`).
 *
 * `.strict()` is load-bearing: it is the mechanical guarantee that a private
 * document can never be written to the public path by mistake. It carries
 * display data only — no amounts, no provider identifiers, no fee fields, no
 * contact data. The authoritative price is served by
 * `GET /api/public/experiences/:publicId/offer`.
 */
export const PublicExperiencePageProjectionSchema = z
    .object({
    schemaVersion: PublicExperienceSchemaVersionSchema,
    publicId: PublicExperiencePublicIdSchema,
    type: PublicExperienceTypeSchema,
    template: PublicExperienceTemplateSchema,
    status: PublicProjectionStatusSchema,
    revision: PublishedRevisionSchema,
    title: ExperienceTitleSchema,
    description: ExperienceDescriptionSchema,
    branding: ExperienceBrandingSchema,
    businessName: z.string().trim().min(1).max(200),
    updatedAt: IsoTimestampSchema,
})
    .strict();
/**
 * Pure private -> public projector. The ONLY sanctioned way to produce the
 * public document.
 *
 * It allowlists fields explicitly (never spreads the private document) and
 * returns `null` when there is nothing publishable yet, so a `DRAFT` experience
 * cannot produce a public page. `businessName` is passed in because it belongs to
 * the business record, not the experience.
 */
export const toPublicExperiencePageProjection = (doc, businessName) => {
    if (doc.status === PublicExperienceStatusEnum.DRAFT || !doc.published) {
        return null;
    }
    return {
        /**
         * The projection has its own version and its shape is unchanged by Part 2
         * (no account id, no client secret), so it stays `1` for v1 AND v2 sources.
         */
        schemaVersion: PUBLIC_EXPERIENCE_SCHEMA_VERSION,
        publicId: doc.publicId,
        type: doc.type,
        template: doc.template,
        status: doc.status,
        revision: doc.published.revision,
        title: doc.published.title,
        description: doc.published.description,
        branding: doc.published.branding,
        businessName,
        updatedAt: doc.updatedAt,
    };
};
/**
 * Admin list row. Intentionally thin: the editor fetches the detail document.
 */
/** Read-only management presentation; never accepted as mutation authority. */
export const PayformDisplayStateSchema = z.enum([
    'DRAFT',
    'LIVE',
    'NEEDS_SETUP',
    'NEEDS_ATTENTION',
    'PAUSED',
    'UNABLE_TO_VERIFY',
]);
export const PayformListFilterSchema = z.enum([
    'ALL',
    'LIVE',
    'NEEDS_ATTENTION',
    'DRAFT',
    'PAUSED',
]);
export const PayformPresentationSchema = z
    .object({
    state: PayformDisplayStateSchema,
    reason: z.string().max(500),
    checkedAt: IsoTimestampSchema.nullable(),
})
    .strict();
export const AdminExperienceSummarySchema = z
    .object({
    ...ExperienceFormClassificationShape,
    experienceId: ExperienceIdSchema,
    publicId: PublicExperiencePublicIdSchema,
    type: PublicExperienceTypeSchema,
    template: PublicExperienceTemplateSchema,
    status: PublicExperienceStatusSchema,
    title: ExperienceTitleSchema,
    draftRevision: RevisionSchema,
    publishedRevision: PublishedRevisionSchema.nullable(),
    /** Amount currently live; `null` while the experience has never published. */
    publishedAmountCents: z.number().int().min(1).nullable(),
    presentation: PayformPresentationSchema.optional(),
    hasUnpublishedChanges: z.boolean().optional(),
    submissionCount: z.number().int().min(0),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
})
    .strict();
/**
 * Admin detail view. Carries both the editable draft and the live snapshot so the
 * editor can show "unpublished changes", plus the preview of exactly what a guest
 * would be served (`previewOffer`) without publishing anything.
 */
export const AdminExperienceDetailSchema = z
    .object({
    ...ExperienceFormClassificationShape,
    experienceId: ExperienceIdSchema,
    businessId: BusinessIdSchema,
    publicId: PublicExperiencePublicIdSchema,
    type: PublicExperienceTypeSchema,
    template: PublicExperienceTemplateSchema,
    status: PublicExperienceStatusSchema,
    draft: ExperienceDraftSchema,
    /** v2 (direct) or v1 (legacy, republish required) live snapshot. */
    published: AnyExperiencePublishedSchema.nullable(),
    /** Server-rendered preview of the DRAFT offer, identical in shape to the
     *  public offer response. Never implies the draft is live. */
    previewOffer: PublicOfferViewSchema,
    /** Absolute public URL built from `POINTPAY_PUBLIC_BASE_URL` + publicId. */
    publicUrl: HttpsUrlSchema,
    hasUnpublishedChanges: z.boolean(),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
    updatedBy: z.string().trim().min(1).max(128),
})
    .strict();
